import React, { useState, useEffect, useRef } from "react";
import { Moon, Sun, RefreshCw, Mail, Settings, Wallet, Power, Star, Download, Menu, X, Unlink, RefreshCcw, LayoutGrid, Receipt, Check, Target, StickyNote } from 'lucide-react';
import PlaidLinkButton from "../DashboardComponents/PlaidLinkButton";
import WaveyIcon from "../Icons/waveyIcon";
import { usePlaidAccounts, useSyncTransactions } from "../../hooks/usePlaid";
import AppModal from "../Modal/AppModal";
import UserAvatar from "../UserAvatar";
import Logo from "../Logo/Logo";

interface MobileMenuProps {
  theme: "light" | "dark";
  toggleTheme: () => void;
  language: "en" | "es";
  t: any;
  currentUser?: {
    name?: string;
    lastName?: string;
    email: string;
    profileImage?: string;
  } | null;
  // Required for Dashboard/ExpensesList, optional for Layout
  hasPaidExpenses?: boolean;
  userTier?: "free" | "standard" | "premium";
  navigate?: (path: string) => void;
  resetAllPaid?: () => void;
  setEmailDialogOpen?: (open: boolean) => void;
  setAiModalOpen?: (open: boolean) => void;
  setToken?: (token: string | null) => void;
  token?: string | null;
  linkToken?: string | null;
  ExportMenuComponent?: React.ReactNode;
  toast?: any;
  refetchExpenses?: () => void;
  onUnlinkBank?: (itemId: string) => void;
  plaidAccounts?: any;
  // For Layout usage
  onLogout?: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  theme,
  toggleTheme,
  language,
  t,
  hasPaidExpenses,
  userTier,
  navigate,
  resetAllPaid,
  setEmailDialogOpen,
  setToken,
  token,
  // linkToken, // Removed unused parameter
  ExportMenuComponent,
  toast,
  refetchExpenses,
  onUnlinkBank,
  plaidAccounts,
  currentUser,
  onLogout,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState<{
    itemId: string;
    bankName: string;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { data: plaidAccountsData } = usePlaidAccounts(token);
  const plaidAccountsToUse = plaidAccounts || plaidAccountsData;
  const { mutate: syncTransactions, isPending: isSyncing } =
    useSyncTransactions(token);
  const hasLinkedBanks = (plaidAccountsToUse?.accounts?.length ?? 0) > 0;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleMenuItemClick = (action: () => void) => {
    setIsOpen(false);
    setTimeout(() => {
      action();
    }, 300);
  };

  const handleSync = () => {
    if (!syncTransactions || !toast || !refetchExpenses) return;
    syncTransactions(undefined, {
      onSuccess: () => {
        toast.success(t.mobileMenu.transactionsSyncSuccess);
        refetchExpenses();
        setSyncModalOpen(false);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || t.mobileMenu.transactionsSyncFailed
        );
        setSyncModalOpen(false);
      },
    });
  };

  // Check if this is Dashboard/ExpensesList context (has navigate) or Layout context
  const isFullMenu = !!navigate;

  return (
    <>
      <div className="mobile-header-bar flex items-center justify-between px-4 py-3 bg-background border-b border-white/10 sticky top-0 z-[1000] md:hidden" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}>
        <div className="flex-shrink-0">
          <Logo theme={theme} title="WaveBudget Tracker" />
        </div>

        {/* Hamburger Button */}
        <button
          onClick={() => setIsOpen(true)}
          className="p-2 cursor-pointer"
          style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)' }}
          aria-label="Open menu"
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className={`fixed inset-0 bg-black/60 z-[1001] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Drawer */}
      <div
        ref={menuRef}
        className={`fixed top-0 right-0 h-full w-[300px] bg-[#1c1c1e]/95 backdrop-blur-2xl border-l border-white/10 z-[1002] transform transition-transform duration-300 ease-out text-white ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            {currentUser && (
              <UserAvatar
                name={currentUser.name}
                lastName={currentUser.lastName}
                profileImage={currentUser.profileImage}
                size={44}
                fontSize="1.1rem"
              />
            )}
            <div>
              <h3 className="m-0 text-lg font-semibold text-white">{t.mobileMenu.menu}</h3>
              {currentUser && (currentUser.name || currentUser.lastName) && (
                <p className="m-0.5 text-sm opacity-70 text-white/60">
                  {currentUser.name && currentUser.lastName
                    ? `${currentUser.name} ${currentUser.lastName}`
                    : currentUser.email}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Menu Items */}
        <div className="p-2">
          {/* Theme Toggle */}
          <button
            onClick={() => handleMenuItemClick(toggleTheme)}
            className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
          >
            <span className="flex items-center justify-center w-6">
              {theme === "light" ? (
                <Moon size={22} />
              ) : (
                <Sun size={22} />
              )}
            </span>
            <span className="text-base font-medium">
              {theme === "light"
                ? t.mobileMenu.switchToDarkMode
                : t.mobileMenu.switchToLightMode}
            </span>
          </button>

          {/* Dashboard - Only show in full menu */}
          {isFullMenu && (
            <button
              onClick={() =>
                handleMenuItemClick(() => navigate?.("/dashboard"))
              }
              className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
            >
              <span className="flex items-center justify-center w-6">
                <LayoutGrid size={22} />
              </span>
              <span className="text-base font-medium">{t.navbar.dashboard}</span>
            </button>
          )}

          {/* Expenses List - Only show in full menu */}
          {isFullMenu && (
            <button
              onClick={() =>
                handleMenuItemClick(() => navigate?.("/expenses-list"))
              }
              className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
            >
              <span className="flex items-center justify-center w-6">
                <Receipt size={22} />
              </span>
              <span className="text-base font-medium">{t.expensesText}</span>
            </button>
          )}

          {/* Planner - Only show in full menu */}
          {isFullMenu && (
            <button
              onClick={() =>
                handleMenuItemClick(() => navigate?.("/planner"))
              }
              className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
            >
              <span className="flex items-center justify-center w-6">
                <Target size={22} />
              </span>
              <span className="text-base font-medium">{t.navbar?.planner || "Planner"}</span>
            </button>
          )}

          {/* Notes - Only show in full menu */}
          {isFullMenu && (
            <button
              onClick={() => handleMenuItemClick(() => navigate?.("/notes"))}
              className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
            >
              <span className="flex items-center justify-center w-6">
                <StickyNote size={22} />
              </span>
              <span className="text-base font-medium">{t.sidebar?.notes || "Notes"}</span>
            </button>
          )}

          {/* Reset All (conditional) - Only show in full menu */}
          {isFullMenu && hasPaidExpenses && resetAllPaid && (
            <button
              onClick={() => handleMenuItemClick(resetAllPaid)}
              className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-orange hover:bg-orange/10 transition-colors"
              style={{ color: '#FF9500' }}
            >
              <span className="flex items-center justify-center w-6">
                <RefreshCw size={22} />
              </span>
              <span className="text-base font-medium">{t.resetAllUnpaid}</span>
            </button>
          )}

          {/* Export Data - Only show in full menu */}
          {isFullMenu && ExportMenuComponent && (
            <div className="flex items-center justify-between w-full px-4 py-3 rounded-xl hover:bg-white/5 transition-colors">
              <div className="flex items-center gap-4">
                <span className="flex items-center justify-center w-6">
                  <Download size={22} />
                </span>
                <span className="text-base font-medium text-white/70">{t.mobileMenu.exportData}</span>
              </div>
              <div className="flex-shrink-0">
                {ExportMenuComponent}
              </div>
            </div>
          )}

          {/* Email Report - Only show in full menu */}
          {isFullMenu && setEmailDialogOpen && (
            <button
              onClick={() =>
                handleMenuItemClick(() => setEmailDialogOpen?.(true))
              }
              className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
            >
              <span className="flex items-center justify-center w-6">
                <Mail size={22} />
              </span>
              <span className="text-base font-medium">{t.mobileMenu.emailReport}</span>
            </button>
          )}

          {/* Settings */}
          <button
            onClick={() => handleMenuItemClick(() => navigate?.("/settings"))}
            className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
          >
            <span className="flex items-center justify-center w-6">
              <Settings size={22} />
            </span>
            <span className="text-base font-medium">{t.settings}</span>
          </button>

          {/* Subscription */}
          <button
            onClick={() =>
              handleMenuItemClick(() => navigate?.("/subscription"))
            }
            className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
          >
            <span className="flex items-center justify-center w-6">
              <Star size={22} />
            </span>
            <span className="text-base font-medium">
              {t.navbar?.subscription || "Subscription"}
            </span>
          </button>

          {/* Bank Link - Show different UI based on link status - Only in full menu */}
          {isFullMenu && userTier === "premium" ? (
            hasLinkedBanks ? (
              <>
                <div className="flex items-center gap-4 w-full px-4 py-3 rounded-xl bg-green-500/10">
                  <span className="flex items-center justify-center w-6">
                    <Wallet size={22} style={{ color: '#34C759' }} />
                  </span>
                  <span className="text-base font-medium text-green flex items-center gap-1.5">
                    <Check size={16} className="text-green flex-shrink-0" />
                    {plaidAccountsToUse?.accounts?.length ?? 0}{" "}
                    {t.mobileMenu.banksLinked}
                  </span>
                </div>

                <button
                  onClick={() =>
                    handleMenuItemClick(() => {
                      setSyncModalOpen(true);
                    })
                  }
                  className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors disabled:opacity-50"
                  disabled={isSyncing}
                >
                  <span className="flex items-center justify-center w-6">
                    <RefreshCcw size={22} className={isSyncing ? "animate-spin" : ""} />
                  </span>
                  <span className="text-base font-medium">
                    {isSyncing
                      ? t.mobileMenu.syncing
                      : t.mobileMenu.syncTransactions}
                  </span>
                </button>

                {plaidAccountsToUse?.accounts &&
                  plaidAccountsToUse.accounts.length > 0 &&
                  onUnlinkBank && (
                    <>
                      {plaidAccountsToUse.accounts.map((bank: any) => (
                        <button
                          key={bank.itemId}
                          onClick={() => {
                            setSelectedBank({
                              itemId: bank.itemId,
                              bankName: bank.bankName,
                            });
                            setUnlinkModalOpen(true);
                          }}
                          className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-red hover:bg-red/10 transition-colors"
                        >
                          <span className="flex items-center justify-center w-6">
                            <Unlink size={22} />
                          </span>
                          <span className="text-base font-medium">
                            {t.plaidLink?.unlinkBank || "Unlink"}{" "}
                            {bank.bankName}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
              </>
            ) : (
              <div className="flex items-center justify-between w-full px-4 py-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="flex items-center gap-4">
                  <span className="flex items-center justify-center w-6">
                    <Wallet size={22} />
                  </span>
                  <span className="text-base font-medium text-white/70">
                    {t.mobileMenu.linkBankAccount}
                  </span>
                </div>
                <div className="flex-shrink-0">
                  <PlaidLinkButton
                    token={token}
                    onSuccess={() => {
                      setIsOpen(false);
                      toast.success(t.mobileMenu.bankLinkedSuccess);
                    }}
                    onExit={() => console.log("User exited Plaid Link")}
                    language={language}
                    userTier={userTier}
                  />
                </div>
              </div>
            )
          ) : (
            <button
              onClick={() =>
                handleMenuItemClick(() => navigate?.("/subscription"))
              }
              className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-4">
                <span className="flex items-center justify-center w-6">
                  <Wallet size={22} />
                </span>
                <span className="text-base font-medium">
                  {t.mobileMenu.upgradeForBankSync}
                </span>
              </div>
              <span className="flex-shrink-0">
                <Star size={18} style={{ color: '#FFD700' }} />
              </span>
            </button>
          )}

          {/* Wavey AI - Only in full menu */}
          {isFullMenu && (
            <button
              onClick={() => handleMenuItemClick(() => navigate?.("/wavey-ai"))}
              className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
            >
              <span className="flex items-center justify-center w-6">
                <WaveyIcon
                  style={{ color: theme === "dark" ? "white" : "black" }}
                />
              </span>
              <span className="text-base font-medium">
                {t.navbar?.waveyAI || "Wavey AI"}
              </span>
            </button>
          )}

          {/* Logout */}
          <button
            onClick={() =>
              handleMenuItemClick(() => {
                if (onLogout) {
                  onLogout();
                } else {
                  setToken?.(null);
                  localStorage.removeItem("token");
                  localStorage.removeItem("biometricCredentialId");
                  navigate?.("/auth");
                }
              })
            }
            className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-red hover:bg-red/10 transition-colors"
          >
            <span className="flex items-center justify-center w-6">
              <Power size={22} />
            </span>
            <span className="text-base font-medium">{t.logout}</span>
          </button>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <p className="m-0 text-xs text-center text-white/40">{t.mobileMenu.waveBudgetPro}</p>
        </div>
      </div>

      {/* Sync Modal */}
      <AppModal
        open={syncModalOpen}
        onClose={() => !isSyncing && setSyncModalOpen(false)}
        title={t.mobileMenu.syncTransactions}
        icon={<RefreshCcw size={20} />}
        actions={{
          primary: {
            label: t.mobileMenu.syncNow,
            onClick: handleSync,
            disabled: isSyncing,
          },
          secondary: {
            label: t.mobileMenu.cancel,
            onClick: () => setSyncModalOpen(false),
            disabled: isSyncing,
          },
        }}
      >
        <p className="mb-2 mt-3 text-white">
          {isSyncing ? (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/20 border-t-primary rounded-full animate-spin" />
              {t.mobileMenu.syncingYourTransactions}
            </div>
          ) : (
            t.mobileMenu.syncModalDescription
          )}
        </p>
        {!isSyncing && (
          <p className="text-sm text-white/60">
            {t.mobileMenu.syncModalNote}
          </p>
        )}
      </AppModal>

      {/* Unlink Bank Modal */}
      <AppModal
        open={unlinkModalOpen}
        onClose={() => setUnlinkModalOpen(false)}
        title={t.plaidLink?.unlinkBankConfirmTitle || "Disconnect Bank?"}
        icon={<Unlink size={20} style={{ color: "#FF3B30" }} />}
        actions={{
          primary: {
            label: t.plaidLink?.disconnectBank || "Disconnect",
            onClick: () => {
              if (selectedBank && onUnlinkBank) {
                onUnlinkBank(selectedBank.itemId);
                setUnlinkModalOpen(false);
                setIsOpen(false);
              }
            },
            color: "error" as const,
          },
          secondary: {
            label: t.mobileMenu.cancel,
            onClick: () => setUnlinkModalOpen(false),
          },
        }}
      >
        <p className="mb-2 mt-3 text-white">
          {t.plaidLink?.unlinkBankConfirmMessage ||
            "This will disconnect your bank and delete all Plaid-synced expenses. This action cannot be undone."}
        </p>
        {selectedBank && (
          <p className="text-sm font-semibold text-red">
            {selectedBank.bankName}
          </p>
        )}
      </AppModal>
    </>
  );
};

export default MobileMenu;
